// src/store/financeContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase/supabaseClient';
import { useAuth } from './authContext';

export const FinanceContext = createContext();

export const FinanceProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settingsId, setSettingsId] = useState(null);
  const [monthlyGoal, setMonthlyGoal] = useState(1000);
  const [cashFlow, setCashFlow] = useState({ income: 0, expenses: 0, profit: 0 });
  const [goalAchieved, setGoalAchieved] = useState(false);
  const [nextMonthBudget, setNextMonthBudget] = useState({
    goal: 1000,
    realProfit: 0,
    distribution: 0
  });
  const [loading, setLoading] = useState(true);

  const syncTrafficExpense = useCallback(async () => {
    if (!currentUser?.id) return;

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString();

    const { data: campaigns, error: campaignError } = await supabase
      .from('marketing_campaigns')
      .select('cost, date')
      .gte('date', firstDay)
      .lte('date', lastDay)
      .eq('user_id', currentUser.id);

    if (campaignError) {
      console.error('Erro ao buscar campanhas de tráfego:', campaignError);
      return;
    }

    const totalCost = campaigns.reduce((sum, c) => sum + (parseFloat(c.cost) || 0), 0);
    if (totalCost <= 0) return;

    const { data: existing, error: existingError } = await supabase
      .from('finance_expenses')
      .select('id')
      .eq('user_id', currentUser.id)
      .eq('category', 'Marketing')
      .ilike('description', '%Tráfego Pago%')
      .gte('date', firstDay)
      .lte('date', lastDay)
      .limit(1);

    if (existingError) {
      console.error('Erro ao verificar despesa de tráfego:', existingError);
      return;
    }

    if (existing && existing.length > 0) {
      console.log('Despesa de tráfego já registrada.');
      return;
    }

    const { error: insertError } = await supabase
      .from('finance_expenses')
      .insert({
        user_id: currentUser.id,
        description: 'Tráfego Pago (automático)',
        category: 'Marketing',
        value: totalCost,
        date: today.toISOString(),
        notes: 'Despesa registrada com base em campanhas de marketing'
      });

    if (insertError) {
      console.error('Erro ao inserir despesa de tráfego:', insertError);
    } else {
      console.log('Despesa de tráfego sincronizada com sucesso.');
    }
  }, [currentUser?.id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (!currentUser || !currentUser.id) {
          setLoading(false);
          return;
        }

        const { data: settingsData } = await supabase
          .from('finance_settings')
          .select('*')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (settingsData) {
          setSettingsId(settingsData.id);
          setMonthlyGoal(settingsData.monthly_goal || 1000);
          setNextMonthBudget({
            goal: settingsData.next_month_goal || 1000,
            realProfit: settingsData.real_profit || 0,
            distribution: settingsData.distribution || 0
          });
        } else {
          const { data: created } = await supabase
            .from('finance_settings')
            .insert({
              user_id: currentUser.id,
              monthly_goal: 1000,
              next_month_goal: 1000,
              real_profit: 0,
              distribution: 0
            })
            .select()
            .single();

          if (created) setSettingsId(created.id);
        }

        const { data: incomeData } = await supabase
          .from('finance_income')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('date', { ascending: false });
        setIncome(incomeData || []);

        const { data: expensesData } = await supabase
          .from('finance_expenses')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('date', { ascending: false });
        setExpenses(expensesData || []);

        await syncTrafficExpense();
      } catch (error) {
        console.error('Erro ao carregar dados financeiros:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, syncTrafficExpense]);

  useEffect(() => {
    const totalIncome = income.reduce((sum, i) => sum + parseFloat(i.value || 0), 0);
    const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.value || 0), 0);
    const profit = totalIncome - totalExpenses;

    setCashFlow({ income: totalIncome, expenses: totalExpenses, profit });
    setGoalAchieved(profit >= monthlyGoal);
    setNextMonthBudget(prev => ({ ...prev, realProfit: profit }));

    if (settingsId) saveFinanceData();
  }, [income, expenses, monthlyGoal, settingsId]);

  const saveFinanceData = useCallback(async () => {
    if (!settingsId) return;
    try {
      await supabase.from('finance_settings').update({
        monthly_goal: monthlyGoal,
        next_month_goal: nextMonthBudget.goal,
        real_profit: nextMonthBudget.realProfit,
        distribution: nextMonthBudget.distribution,
        updated_at: new Date().toISOString()
      }).eq('id', settingsId);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    }
  }, [settingsId, monthlyGoal, nextMonthBudget]);

  const addIncome = async (entry) => {
    try {
      if (!currentUser) return false;
      await supabase.from('finance_income').insert([{ ...entry, user_id: currentUser.id, value: parseFloat(entry.value), created_at: new Date().toISOString() }]);
      return true;
    } catch (error) {
      console.error('Erro ao adicionar receita:', error);
      return false;
    }
  };

  const addExpense = async (entry) => {
    try {
      if (!currentUser) return false;
      await supabase.from('finance_expenses').insert([{ ...entry, user_id: currentUser.id, value: parseFloat(entry.value), created_at: new Date().toISOString() }]);
      return true;
    } catch (error) {
      console.error('Erro ao adicionar despesa:', error);
      return false;
    }
  };

  const removeIncome = async (id) => {
    try {
      await supabase.from('finance_income').delete().eq('id', id);
      return true;
    } catch (error) {
      console.error('Erro ao remover receita:', error);
      return false;
    }
  };

  const removeExpense = async (id) => {
    try {
      await supabase.from('finance_expenses').delete().eq('id', id);
      return true;
    } catch (error) {
      console.error('Erro ao remover despesa:', error);
      return false;
    }
  };

  const updateMonthlyGoal = async (value) => {
    try {
      const newGoal = parseFloat(value);
      setMonthlyGoal(newGoal);
      if (settingsId) {
        await supabase.from('finance_settings')
          .update({ monthly_goal: newGoal, updated_at: new Date().toISOString() })
          .eq('id', settingsId);
      }
      setNextMonthBudget(prev => ({ ...prev, goal: newGoal }));
      return true;
    } catch (error) {
      console.error('Erro ao atualizar meta:', error);
      return false;
    }
  };

  const updateDistribution = async (value) => {
    try {
      const newDist = parseFloat(value);
      setNextMonthBudget(prev => ({ ...prev, distribution: newDist }));
      if (settingsId) {
        await supabase.from('finance_settings')
          .update({ distribution: newDist, updated_at: new Date().toISOString() })
          .eq('id', settingsId);
      }
      return true;
    } catch (error) {
      console.error('Erro ao atualizar distribuição:', error);
      return false;
    }
  };

  return (
    <FinanceContext.Provider value={{
      income, expenses, cashFlow, monthlyGoal,
      goalAchieved, nextMonthBudget, loading,
      addIncome, addExpense, removeIncome, removeExpense,
      updateMonthlyGoal, updateDistribution,
      syncTrafficExpense
    }}>
      {children}
    </FinanceContext.Provider>
  );
};
